import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LatestVersions, Machine } from '@couch/shared';
import { api, errorMessage } from '../../api/client';
import { MACHINES_KEY } from './MachineActions';

/** The first agent with `update_agent`; older ones re-run the install line once (doc 16). */
const SELF_UPDATE_SINCE = '0.2.0';

/** "0.10.0" is newer than "0.9.2": compares number by number; anything unreadable counts as 0. */
export function isOlder(installed: string, latest: string): boolean {
  const parts = (v: string) => v.split('.').map((n) => Number.parseInt(n, 10) || 0);
  const [a, b] = [parts(installed), parts(latest)];
  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    const diff = (a[i] ?? 0) - (b[i] ?? 0);
    if (diff !== 0) return diff < 0;
  }
  return false;
}

/** Updates right on the card (doc 11 B1) — on the machine page alone they were missed. */
export function MachineUpdates({ machine, latest }: { machine: Machine; latest: LatestVersions | undefined }) {
  const queryClient = useQueryClient();
  const done = () => queryClient.invalidateQueries({ queryKey: MACHINES_KEY });
  const agent = useMutation({ mutationFn: () => api.updateAgent(machine.id), onSuccess: done });
  const comfy = useMutation({ mutationFn: () => api.updateComfyui(machine.id), onSuccess: done });
  if (!latest || !machine.online) return null;
  const agentOld = machine.agentVersion !== null && isOlder(machine.agentVersion, latest.agent);
  const comfyOld = machine.comfyuiVersion !== null && isOlder(machine.comfyuiVersion, latest.comfyui);
  if (!agentOld && !comfyOld && !agent.isSuccess) return null;
  const selfUpdates = machine.agentVersion !== null && !isOlder(machine.agentVersion, SELF_UPDATE_SINCE);
  const error = agent.error ?? comfy.error;
  return (
    <div className="machine-updates">
      {agentOld && <p className="muted">Agent {machine.agentVersion} → {latest.agent}</p>}
      {agentOld && !selfUpdates && (
        <p className="warning">This agent cannot update itself: run the install line once more on this machine — it stays the same machine in Couch.</p>
      )}
      <div className="machine-actions">
        {agentOld && selfUpdates && (
          <button type="button" disabled={agent.isPending} onClick={() => { agent.mutate(); }}>Update agent</button>
        )}
        {comfyOld && (
          <button type="button" disabled={comfy.isPending} onClick={() => { comfy.mutate(); }}>{comfy.isPending ? 'Updating ComfyUI…' : `Update ComfyUI to ${latest.comfyui}`}</button>
        )}
      </div>
      {agent.isSuccess && <p role="status" className="success">The agent is updating and reconnects in a few seconds.</p>}
      {error && <p role="alert" className="form-error">{errorMessage(error)}</p>}
    </div>
  );
}
